<template>
  <div class="guide-documents">
    <h2>What are documents?</h2>
    <p>
      Documents are the individual files within a package that signers review and sign.
      A single package can contain multiple documents, for example a contract, an NDA,
      and a disclosure form bundled together in one signing transaction. Each document
      has its own set of signing fields. All documents in a package share the same
      signers and follow the same lifecycle.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Supported formats and limits</h2>
    <p>The platform accepts three document formats:</p>
    <ul>
      <li>
        <strong>PDF.</strong> Native format, preferred. What you upload is exactly
        what signers see in the signing viewer.
      </li>
      <li>
        <strong>DOCX.</strong> Converted to PDF server-side before the signing
        ceremony. Convenient for template-driven workflows, though complex formatting
        may shift during conversion.
      </li>
      <li>
        <strong>DOC.</strong> Legacy Word format, also converted to PDF server-side.
        Prefer DOCX when you control the source. DOC support exists for compatibility.
      </li>
    </ul>
    <p>
      Size limits are 25 MB per file decoded, 100 MB total per request body, and
      10 MB per chunked-upload block.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <strong>Recommendation:</strong> Use PDF for predictable layout. If your workflow
      generates DOCX templates (for example from a CRM or document assembly tool), test
      the converted output once to verify formatting before scaling up.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Working with documents in the portal</h2>
    <p>
      When you create a package in the portal, the <strong>Documents</strong> step of
      the builder lets you drag-and-drop files or browse for them. Each file uploads
      immediately and processes in the background. A spinner appears next to each file
      until it is ready for field placement. DOCX files are auto-converted to PDF, and
      you can preview the converted result before sending.
    </p>
    <p>
      Once documents are added to a package, use the builder's document list to
      reorder, rename, or remove them. If a signer has fields placed on a document you
      remove, the builder warns you about orphaned signers before the deletion
      completes.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Programmatic callers have three upload paths available: chunked, direct
      multipart, and URL import. The full download, removal-impact, and reprocessing
      surface is covered below. Every operation available in the portal has an
      equivalent REST endpoint and MCP tool.
    </p>

    <h3>Three upload paths</h3>
    <p>Pick the path that fits your situation:</p>
    <ul>
      <li>
        <strong>Chunked upload</strong> (recommended for larger files or unreliable
        networks). Three-step flow: initiate, stage blocks, commit. Covered below.
      </li>
      <li>
        <strong>Direct multipart upload.</strong> Single-request convenience endpoint
        for smaller files. Use when you have the complete file in memory and prefer
        one call.
      </li>
      <li>
        <strong>URL import.</strong> The API fetches the file itself from an HTTPS URL
        you provide. Use when the file already lives in cloud storage (S3, GCS, Azure
        Blob) and you prefer to avoid proxying bytes through your client.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Chunked upload flow</h3>
    <p>
      Documents upload using a chunked protocol rather than a single multipart POST.
      The design addresses two practical concerns:
    </p>
    <ul>
      <li>
        <strong>Reliability.</strong> Large files (10 MB or more) are prone to network
        timeouts and gateway limits. Chunking breaks the file into manageable pieces
        so a transient failure only affects one chunk, not the entire upload.
      </li>
      <li>
        <strong>Resumability.</strong> If a network interruption occurs mid-upload,
        check which blocks have already been staged and resume from where you left
        off instead of starting over.
      </li>
    </ul>

    <h3>Step 1. Initiate the upload</h3>
    <p>
      Create an upload session for the package. The response gives you a <code>sessionGuid</code>
      to reference in subsequent calls and an expiration timestamp (sessions expire if left idle).
    </p>
    <GuideCodeTabs :samples="samples.initiateUpload" />
    <p>Response:</p>
    <GuideCodeTabs :samples="samples.initiateResponse" />

    <h3>Step 2. Stage blocks</h3>
    <p>
      Split your file into 10 MB chunks and upload each one sequentially. The
      <code>blockIndex</code> is zero-based (0, 1, 2, and so on). The request body is
      raw binary. Set the content type to <code>application/octet-stream</code>.
    </p>
    <GuideCodeTabs :samples="samples.stageBlocks" />

    <h3>Step 3. Commit</h3>
    <p>
      Once all blocks are staged, commit the upload. The server assembles the blocks
      into a single file, creates the document record in the package, and triggers the
      server-side processing pipeline.
    </p>
    <GuideCodeTabs :samples="samples.commitUpload" />

    <h3>Step 4. Abort (optional)</h3>
    <p>
      If something went wrong and you want to cancel, delete the upload session. This
      cleans up any staged blocks and frees storage.
    </p>
    <GuideCodeTabs :samples="samples.abortUpload" />

    <h3>Step 5. Check status</h3>
    <p>
      At any point you can query the upload session to see how many blocks have been
      committed and the current status.
    </p>
    <GuideCodeTabs :samples="samples.uploadStatus" />

    <q-separator class="q-my-lg" />

    <h3>Direct multipart upload</h3>
    <p>
      For small files when the chunked flow would be overkill, post the file directly
      as multipart form data. The single request replaces initiate, stage, and commit.
    </p>
    <GuideCodeTabs :samples="samples.directUpload" />
    <p>
      The same 25 MB per-file and 100 MB total-request limits apply. The response
      matches the commit response from the chunked flow, with document GUID, content
      hash, and initial processing status.
    </p>

    <q-separator class="q-my-lg" />

    <h3>URL import</h3>
    <p>
      Hand the API an HTTPS URL and have it pull the file server-side. Useful for
      documents already hosted in cloud storage or a CMS.
    </p>
    <GuideCodeTabs :samples="samples.urlImport" />
    <ul>
      <li><strong>HTTPS only.</strong> HTTP URLs are rejected.</li>
      <li><strong>File name.</strong> By default the server derives the file name from the URL path, which works for clean URLs like <code>.../contract.pdf</code> and presigned S3, GCS, or Azure SAS links (the query string is ignored). For URLs whose path does not carry the file name - Google Drive download links (<code>https://drive.google.com/uc?export=download&amp;id=...</code>), SharePoint anonymous-share URLs, many CMS download endpoints - supply the optional <code>fileName</code> field on the request body (e.g. <code>"fileName": "Q3-Contract.pdf"</code>). The extension still must be <code>.pdf</code>, <code>.doc</code>, or <code>.docx</code>; content is verified against magic bytes regardless.</li>
      <li><strong>SSRF-hardened.</strong> Private IPs, loopback, and link-local addresses are rejected. The server will not fetch from internal network destinations.</li>
      <li><strong>Rate-limited</strong> to 10 requests per minute per tenant under the <code>url-import</code> policy.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Processing pipeline</h3>
    <p>
      After you commit an upload, the document moves through a server-side processing
      pipeline before it is ready for field placement and signing. The
      <code>processingStatus</code> field on the document tracks progress:
    </p>
    <table class="q-table q-mb-md" style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Status</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">What happens</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Pending</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Upload committed. The document is queued for processing.</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Processing</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Active processing runs DOCX to PDF conversion (if needed), page-image generation, and optional text-tag detection.</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Ready</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Document is fully processed and ready for field placement and signing.</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Failed</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #eee;">Processing encountered an error. Inspect the <code>processingError</code> field for details. Emits a <code>document.processing_failed</code> webhook event.</td>
        </tr>
      </tbody>
    </table>
    <p>
      On success the document emits a <code>document.processed</code> webhook.
      Subscribe to those events instead of polling when you can.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <strong>Tip:</strong> Either poll the document status periodically
      (<code>GET /api/v1/packages/{packageId}/documents/{documentId}</code>) or
      register a webhook to be notified when processing completes. Webhooks are the
      recommended approach for production integrations.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h3>Content deduplication</h3>
    <p>
      The platform computes a SHA-256 hash of every uploaded file. If you upload the
      same file to multiple packages (for example, a standard terms-and-conditions
      document reused across many signing transactions), the system detects the
      matching hash and reuses the already-processed version. Two benefits follow:
    </p>
    <ul>
      <li><strong>Faster turnaround.</strong> The document skips the conversion and tag detection steps because they were already completed.</li>
      <li><strong>Less storage cost.</strong> The platform stores one copy of the file content, not a duplicate for every package.</li>
    </ul>
    <p>
      Deduplication is automatic and transparent. You do not need to change your
      upload logic. Upload normally and the system handles the rest.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Downloads</h3>
    <p>
      Once a package has been sent (or completed), three download variants are
      available for each document:
    </p>
    <ul>
      <li><strong>Original.</strong> The file exactly as it was uploaded (PDF or DOCX).</li>
      <li><strong>PDF.</strong> The PDF version of the document. For native PDFs this matches the original. For DOCX uploads this is the converted PDF before any annotations are applied.</li>
      <li><strong>Signed.</strong> The final PDF with all annotations, including signatures, initials, text fields, dates, and stamps, permanently burned into the document. This is the legally executed copy.</li>
    </ul>
    <p>Each variant has its own endpoint:</p>
    <GuideCodeTabs :samples="samples.downloads" />
    <p>
      Each call returns <code>{ url, expiresOn }</code>. The <code>url</code> is a
      time-limited pre-signed URL. Download it immediately, do not cache it, and
      request a fresh URL the next time you need to download. For the
      combined-documents final PDF (all documents as one signed file), use
      <code>GET /api/v1/packages/{packageId}/download</code> at the package level.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Document removal impact</h3>
    <p>
      Before removing a document from a draft package, check the downstream impact.
      If signers have fields placed only on the document you are about to delete,
      those signers become <em>orphaned</em>. They exist in the package but have
      nothing to sign.
    </p>
    <GuideCodeTabs :samples="samples.removalImpact" />
    <p>Response:</p>
    <GuideCodeTabs :samples="samples.removalImpactResponse" />
    <p>
      Use this information to decide whether to reassign fields, add fields on remaining documents,
      or remove the orphaned signers before proceeding with the deletion.
    </p>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Documents</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

// Helper for simple GET calls with no body. Useful for status / download / removal-impact.
function getSamples(pathTemplate) {
    return {
        curl: `curl ${API}${pathTemplate.replace('{packageId}', '$PACKAGE_ID').replace('{sessionGuid}', '$SESSION_GUID').replace('{documentId}', '$DOCUMENT_ID')} \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}${pathTemplate.replace(/\{(\w+)\}/g, '${$1}')}\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const result = await response.json();`,

        node: `const response = await fetch(
  \`${API}${pathTemplate.replace(/\{(\w+)\}/g, '${$1}')}\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const result = await response.json();`,

        python: `response = requests.get(
    f'${API}${pathTemplate.replace(/\{(\w+)\}/g, (_, name) => `{${name.replace(/([A-Z])/g, '_$1').toLowerCase()}}`)}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
result = response.json()`,

        csharp: `var result = await client.GetFromJsonAsync<JsonElement>(
    $"${pathTemplate.replace('/api/', 'api/').replace(/\{(\w+)\}/g, '{$1}')}");`,

        php: `$response = $client->get("${pathTemplate.replace('/api/', 'api/').replace(/\{(\w+)\}/g, '{$$$1}')}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}${pathTemplate.replace(/\{(\w+)\}/g, (_, name) => `#{${name.replace(/([A-Z])/g, '_$1').toLowerCase()}}`)}")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
result = JSON.parse(res.body)`
    };
}

const samples = {
    initiateUpload: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/uploads \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "fileName": "contract.pdf", "totalSizeBytes": 2097152 }'`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, totalSizeBytes: file.size })
  }
);
const { sessionGuid, expiresOn } = await response.json();`,

        node: `const fs = require('node:fs/promises');
const stats = await fs.stat('contract.pdf');

const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ fileName: 'contract.pdf', totalSizeBytes: stats.size })
  }
);
const { sessionGuid, expiresOn } = await response.json();`,

        python: `import os, requests

size = os.path.getsize('contract.pdf')
response = requests.post(
    f'${API}/api/v1/packages/{package_id}/uploads',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'fileName': 'contract.pdf', 'totalSizeBytes': size}
)
response.raise_for_status()
session_guid = response.json()['sessionGuid']`,

        csharp: `var fileInfo = new FileInfo("contract.pdf");

var response = await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/uploads",
    new { fileName = fileInfo.Name, totalSizeBytes = fileInfo.Length });
response.EnsureSuccessStatusCode();
var sessionGuid = (await response.Content.ReadFromJsonAsync<JsonElement>())
    .GetProperty("sessionGuid").GetString();`,

        php: `$size = filesize('contract.pdf');

$response = $client->post("api/v1/packages/{$packageId}/uploads", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['fileName' => 'contract.pdf', 'totalSizeBytes' => $size],
]);
$sessionGuid = json_decode($response->getBody(), true)['sessionGuid'];`,

        ruby: `size = File.size('contract.pdf')

uri = URI("${API}/api/v1/packages/#{package_id}/uploads")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { fileName: 'contract.pdf', totalSizeBytes: size }.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
session_guid = JSON.parse(res.body)['sessionGuid']`
    },

    initiateResponse: {
        json: `{
  "sessionGuid": "a1b2c3d4-5e6f-7a8b-9c0d-1e2f3a4b5c6d",
  "expiresOn": "2026-03-02T12:00:00Z"
}`
    },

    stageBlocks: {
        curl: `# Split the file into 10 MB chunks and PUT each one.
# blockIndex is zero-based; blocks may be uploaded out of order.
curl -X PUT ${API}/api/v1/packages/$PACKAGE_ID/uploads/$SESSION_GUID/blocks/0 \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/octet-stream" \\
  --data-binary @chunk-0.bin

curl -X PUT ${API}/api/v1/packages/$PACKAGE_ID/uploads/$SESSION_GUID/blocks/1 \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/octet-stream" \\
  --data-binary @chunk-1.bin`,

        js: `const CHUNK = 10 * 1024 * 1024;
for (let i = 0, idx = 0; i < file.size; i += CHUNK, idx++) {
  await fetch(
    \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}/blocks/\${idx}\`,
    {
      method: 'PUT',
      headers: {
        'Authorization': 'Bearer ' + ESIGN_TOKEN,
        'Content-Type': 'application/octet-stream'
      },
      body: file.slice(i, i + CHUNK)
    }
  );
}`,

        node: `const fs = require('node:fs/promises');
const data = await fs.readFile('contract.pdf');
const CHUNK = 10 * 1024 * 1024;

for (let i = 0, idx = 0; i < data.length; i += CHUNK, idx++) {
  const end = Math.min(i + CHUNK, data.length);
  await fetch(
    \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}/blocks/\${idx}\`,
    {
      method: 'PUT',
      headers: {
        'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
        'Content-Type': 'application/octet-stream'
      },
      body: data.subarray(i, end)
    }
  );
}`,

        python: `CHUNK = 10 * 1024 * 1024

with open('contract.pdf', 'rb') as f:
    data = f.read()

for idx, start in enumerate(range(0, len(data), CHUNK)):
    requests.put(
        f'${API}/api/v1/packages/{package_id}/uploads/{session_guid}/blocks/{idx}',
        headers={
            'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}",
            'Content-Type': 'application/octet-stream'
        },
        data=data[start:start + CHUNK]
    ).raise_for_status()`,

        csharp: `const int CHUNK = 10 * 1024 * 1024;
var bytes = await File.ReadAllBytesAsync("contract.pdf");

for (int idx = 0, start = 0; start < bytes.Length; idx++, start += CHUNK)
{
    var end = Math.Min(start + CHUNK, bytes.Length);
    using var content = new ByteArrayContent(bytes, start, end - start);
    content.Headers.ContentType = new("application/octet-stream");

    var put = await client.PutAsync(
        $"api/v1/packages/{packageId}/uploads/{sessionGuid}/blocks/{idx}",
        content);
    put.EnsureSuccessStatusCode();
}`,

        php: `$data = file_get_contents('contract.pdf');
$chunk = 10 * 1024 * 1024;

for ($i = 0, $idx = 0; $i < strlen($data); $i += $chunk, $idx++) {
    $client->put(
        "api/v1/packages/{$packageId}/uploads/{$sessionGuid}/blocks/{$idx}",
        [
            'headers' => [
                'Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN'),
                'Content-Type' => 'application/octet-stream'
            ],
            'body' => substr($data, $i, $chunk)
        ]
    );
}`,

        ruby: `CHUNK = 10 * 1024 * 1024
data = File.binread('contract.pdf')

(0...data.bytesize).step(CHUNK).with_index do |start, idx|
  uri = URI("${API}/api/v1/packages/#{package_id}/uploads/#{session_guid}/blocks/#{idx}")
  req = Net::HTTP::Put.new(uri)
  req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  req['Content-Type'] = 'application/octet-stream'
  req.body = data.byteslice(start, CHUNK)
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
end`
    },

    commitUpload: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/uploads/$SESSION_GUID/commit \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "displayName": "Residential Lease", "displayOrder": 1 }'`,

        js: `const commit = await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}/commit\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName: 'Residential Lease', displayOrder: 1 })
  }
);
const { documentGuid, contentHash, processingStatus } = await commit.json();`,

        node: `const commit = await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}/commit\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ displayName: 'Residential Lease', displayOrder: 1 })
  }
);
const { documentGuid, contentHash, processingStatus } = await commit.json();`,

        python: `commit = requests.post(
    f'${API}/api/v1/packages/{package_id}/uploads/{session_guid}/commit',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'displayName': 'Residential Lease', 'displayOrder': 1}
)
commit.raise_for_status()
document_guid = commit.json()['documentGuid']`,

        csharp: `var commit = await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/uploads/{sessionGuid}/commit",
    new { displayName = "Residential Lease", displayOrder = 1 });
commit.EnsureSuccessStatusCode();
var body = await commit.Content.ReadFromJsonAsync<JsonElement>();
var documentGuid = body.GetProperty("documentGuid").GetString();`,

        php: `$commit = $client->post(
    "api/v1/packages/{$packageId}/uploads/{$sessionGuid}/commit",
    [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
        'json' => ['displayName' => 'Residential Lease', 'displayOrder' => 1]
    ]
);
$documentGuid = json_decode($commit->getBody(), true)['documentGuid'];`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/uploads/#{session_guid}/commit")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { displayName: 'Residential Lease', displayOrder: 1 }.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
document_guid = JSON.parse(res.body)['documentGuid']`
    },

    abortUpload: {
        curl: `curl -X DELETE ${API}/api/v1/packages/$PACKAGE_ID/uploads/$SESSION_GUID \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}\`,
  { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);`,

        node: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}\`,
  { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);`,

        python: `requests.delete(
    f'${API}/api/v1/packages/{package_id}/uploads/{session_guid}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
).raise_for_status()`,

        csharp: `await client.DeleteAsync(
    $"api/v1/packages/{packageId}/uploads/{sessionGuid}");`,

        php: `$client->delete("api/v1/packages/{$packageId}/uploads/{$sessionGuid}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/uploads/#{session_guid}")
req = Net::HTTP::Delete.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    uploadStatus: {
        curl: `curl ${API}/api/v1/packages/$PACKAGE_ID/uploads/$SESSION_GUID \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const status = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/uploads/\${sessionGuid}\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const status = await response.json();`,

        python: `status = requests.get(
    f'${API}/api/v1/packages/{package_id}/uploads/{session_guid}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
).json()`,

        csharp: `var status = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/packages/{packageId}/uploads/{sessionGuid}");`,

        php: `$response = $client->get("api/v1/packages/{$packageId}/uploads/{$sessionGuid}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$status = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/uploads/#{session_guid}")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
status = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    directUpload: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/documents \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -F "file=@contract.pdf" \\
  -F "displayName=Residential Lease" \\
  -F "displayOrder=1"`,

        js: `// In a browser, pass a File from <input type="file">.
const form = new FormData();
form.append('file', file);
form.append('displayName', 'Residential Lease');
form.append('displayOrder', '1');

const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN }, // no Content-Type - browser sets multipart boundary
    body: form
  }
);
const document = await response.json();`,

        node: `const fs = require('node:fs');
const form = new FormData();
form.append('file', new Blob([await fs.promises.readFile('contract.pdf')]), 'contract.pdf');
form.append('displayName', 'Residential Lease');
form.append('displayOrder', '1');

const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN },
    body: form
  }
);
const document = await response.json();`,

        python: `with open('contract.pdf', 'rb') as f:
    response = requests.post(
        f'${API}/api/v1/packages/{package_id}/documents',
        headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
        files={'file': f},
        data={'displayName': 'Residential Lease', 'displayOrder': 1}
    )
response.raise_for_status()
document = response.json()`,

        csharp: `using var form = new MultipartFormDataContent();
form.Add(new StreamContent(File.OpenRead("contract.pdf")), "file", "contract.pdf");
form.Add(new StringContent("Residential Lease"), "displayName");
form.Add(new StringContent("1"), "displayOrder");

var response = await client.PostAsync($"api/v1/packages/{packageId}/documents", form);
response.EnsureSuccessStatusCode();
var document = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post("api/v1/packages/{$packageId}/documents", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'multipart' => [
        ['name' => 'file',        'contents' => fopen('contract.pdf', 'r'), 'filename' => 'contract.pdf'],
        ['name' => 'displayName', 'contents' => 'Residential Lease'],
        ['name' => 'displayOrder','contents' => '1'],
    ],
]);
$document = json_decode($response->getBody(), true);`,

        ruby: `require 'net/http/post/multipart'

uri = URI("${API}/api/v1/packages/#{package_id}/documents")
req = Net::HTTP::Post::Multipart.new(uri.path,
  'file' => UploadIO.new('contract.pdf', 'application/pdf'),
  'displayName' => 'Residential Lease',
  'displayOrder' => '1'
)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
document = JSON.parse(res.body)`
    },

    urlImport: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/documents/import-url \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "url": "https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...",
    "displayName": "Residential Lease"
  }'`,

        js: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents/import-url\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: 'https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...',
      displayName: 'Residential Lease'
    })
  }
);`,

        node: `await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents/import-url\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      url: 'https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...',
      displayName: 'Residential Lease'
    })
  }
);`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/documents/import-url',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'url': 'https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...',
        'displayName': 'Residential Lease'
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/documents/import-url",
    new
    {
        url = "https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...",
        displayName = "Residential Lease"
    });`,

        php: `$client->post("api/v1/packages/{$packageId}/documents/import-url", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'url' => 'https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...',
        'displayName' => 'Residential Lease',
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/documents/import-url")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  url: 'https://bucket.s3.amazonaws.com/contracts/lease-2026.pdf?X-Amz-Signature=...',
  displayName: 'Residential Lease'
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    downloads: {
        curl: `# Original file (as uploaded, PDF or DOCX)
curl ${API}/api/v1/packages/$PACKAGE_ID/documents/$DOC_ID/download \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# PDF version (no annotations; converted from DOCX if needed)
curl ${API}/api/v1/packages/$PACKAGE_ID/documents/$DOC_ID/download-pdf \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# Signed PDF (annotations burned in; Completed packages only)
curl ${API}/api/v1/packages/$PACKAGE_ID/documents/$DOC_ID/download-signed \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const h = { 'Authorization': 'Bearer ' + ESIGN_TOKEN };
const base = \`${API}/api/v1/packages/\${packageId}/documents/\${docId}\`;

const original = await (await fetch(\`\${base}/download\`,        { headers: h })).json();
const asPdf    = await (await fetch(\`\${base}/download-pdf\`,    { headers: h })).json();
const signed   = await (await fetch(\`\${base}/download-signed\`, { headers: h })).json();
// Each returns { url, expiresOn }`,

        node: `const h = { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN };
const base = \`${API}/api/v1/packages/\${packageId}/documents/\${docId}\`;

const original = await (await fetch(\`\${base}/download\`,        { headers: h })).json();
const asPdf    = await (await fetch(\`\${base}/download-pdf\`,    { headers: h })).json();
const signed   = await (await fetch(\`\${base}/download-signed\`, { headers: h })).json();`,

        python: `h = {'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
base = f'${API}/api/v1/packages/{package_id}/documents/{doc_id}'

original = requests.get(f'{base}/download',        headers=h).json()
as_pdf   = requests.get(f'{base}/download-pdf',    headers=h).json()
signed   = requests.get(f'{base}/download-signed', headers=h).json()`,

        csharp: `var basePath = $"api/v1/packages/{packageId}/documents/{docId}";

var original = await client.GetFromJsonAsync<JsonElement>($"{basePath}/download");
var asPdf    = await client.GetFromJsonAsync<JsonElement>($"{basePath}/download-pdf");
var signed   = await client.GetFromJsonAsync<JsonElement>($"{basePath}/download-signed");`,

        php: `$h = ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]];
$base = "api/v1/packages/{$packageId}/documents/{$docId}";

$original = json_decode($client->get("{$base}/download",        $h)->getBody(), true);
$asPdf    = json_decode($client->get("{$base}/download-pdf",    $h)->getBody(), true);
$signed   = json_decode($client->get("{$base}/download-signed", $h)->getBody(), true);`,

        ruby: `def get_json(path)
  uri = URI("${API}#{path}")
  req = Net::HTTP::Get.new(uri)
  req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  JSON.parse(Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body)
end

base = "/api/v1/packages/#{package_id}/documents/#{doc_id}"
original = get_json("#{base}/download")
as_pdf   = get_json("#{base}/download-pdf")
signed   = get_json("#{base}/download-signed")`
    },

    removalImpact: {
        curl: `curl ${API}/api/v1/packages/$PACKAGE_ID/documents/$DOC_ID/removal-impact \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents/\${docId}/removal-impact\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { orphanedSigners } = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/documents/\${docId}/removal-impact\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { orphanedSigners } = await response.json();`,

        python: `response = requests.get(
    f'${API}/api/v1/packages/{package_id}/documents/{doc_id}/removal-impact',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
orphans = response.json()['orphanedSigners']`,

        csharp: `var impact = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/packages/{packageId}/documents/{docId}/removal-impact");`,

        php: `$response = $client->get(
    "api/v1/packages/{$packageId}/documents/{$docId}/removal-impact",
    ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]]
);
$orphans = json_decode($response->getBody(), true)['orphanedSigners'];`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/documents/#{doc_id}/removal-impact")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
orphans = JSON.parse(res.body)['orphanedSigners']`
    },

    removalImpactResponse: {
        json: `{
  "orphanedSigners": [
    {
      "signerId": "signer-guid",
      "name": "Jane Doe",
      "email": "jane@example.com"
    }
  ]
}`
    }
};

export default {
    name: 'GuideDocuments',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>

